import { Injector } from '@angular/core';
import {
  FormioCustomComponentInfo,
  registerCustomFormioComponent
} from '@evi-ui/angular';
import { Components } from '@evi-ui/js';
import { ImageByIdComponent } from './image-by-id.component';

const COMPONENT_OPTIONS: FormioCustomComponentInfo = {
  type: 'image-by-id',
  selector: 'image-by-id',
  title: 'Image By Id',
  group: 'basic',
  icon: 'fa-regular fa-image',
  emptyValue: '',
  editForm: editForm
};

function editForm() {
  return {
    components: [
      { key: 'type', type: 'hidden' },
      {
        label: 'Tabs',
        components: [displayTab(), dataTab(), conditionalTab()],
        key: 'tabs',
        type: 'tabs',
        input: false,
        tableView: false
      }
    ]
  };
}

function displayTab() {
  return {
    key: 'display',
    label: 'Display',
    components: [
      {
        weight: 0,
        type: 'textfield',
        input: true,
        key: 'label',
        label: 'Label',
        placeholder: 'Field Label',
        tooltip: 'The label for this field that will appear next to it.',
        validate: {
          required: true
        }
      },
      {
        weight: 10,
        type: 'textfield',
        input: true,
        key: 'customOptions.altText',
        label: 'Alt Text',
        placeholder: 'Image alt text'
      },
      {
        weight: 20,
        type: 'number',
        input: true,
        key: 'customOptions.imageWidth',
        label: 'Image Width (px)',
        tooltip: 'Leave blank to render at natural size.'
      },
      {
        weight: 30,
        type: 'textfield',
        input: true,
        key: 'customOptions.emptyMessage',
        label: 'Empty Message',
        placeholder: 'No image',
        tooltip: 'Message shown when there is no image id to display.'
      },
      {
        weight: 40,
        type: 'checkbox',
        input: true,
        key: 'customOptions.showDownloadButton',
        label: 'Show Download Button',
        tooltip:
          'Overlays a download button on the image that downloads the original file.'
      },
      {
        weight: 41,
        type: 'select',
        input: true,
        key: 'customOptions.downloadButtonPosition',
        label: 'Download Button Position',
        defaultValue: 'top-right',
        dataSrc: 'values',
        data: {
          values: [
            { label: 'Top Left', value: 'top-left' },
            { label: 'Top Right', value: 'top-right' },
            { label: 'Bottom Left', value: 'bottom-left' },
            { label: 'Bottom Right', value: 'bottom-right' },
            { label: 'Center', value: 'center' }
          ]
        },
        conditional: {
          json: {
            '===': [{ var: 'data.customOptions.showDownloadButton' }, true]
          }
        }
      },
      {
        weight: 42,
        type: 'textfield',
        input: true,
        key: 'customOptions.downloadApiUrl',
        label: 'Download API URL',
        placeholder: '/api/core/documents/${imageId}/download',
        description:
          'Optional. URL the download button calls to fetch the original file. Use ${imageId} as a placeholder for the resolved image id. Leave blank to use the default documents/{id}/download endpoint.',
        conditional: {
          json: {
            '===': [{ var: 'data.customOptions.showDownloadButton' }, true]
          }
        }
      },
      {
        type: 'textfield',
        label: 'ACL Key',
        key: 'aclResource',
        weight: 5,
        placeholder: 'ACL Key',
        input: true
      },
      {
        weight: 1100,
        type: 'checkbox',
        label: 'Hidden',
        tooltip:
          'A hidden field is still a part of the form, but is hidden from view.',
        key: 'hidden',
        input: true
      },
      {
        weight: 1200,
        type: 'checkbox',
        label: 'Hide Label',
        tooltip:
          'Hide the label (title, if no label) of this component. This allows you to show the label in the form builder, but not when it is rendered.',
        key: 'hideLabel',
        input: true
      },
      {
        weight: 1400,
        type: 'checkbox',
        label: 'Disabled',
        tooltip: 'Disable the form input.',
        key: 'disabled',
        input: true
      }
    ]
  };
}

function dataTab() {
  return {
    key: 'data',
    label: 'Data',
    components: [
      {
        weight: 0,
        type: 'textfield',
        input: true,
        key: 'key',
        label: 'Property Name',
        tooltip:
          "The name of this field in the API endpoint. When 'Image Id Field' below is left blank, this field's own value is used as the image/document id.",
        validate: {
          pattern: String.raw`(\w|\w[\w-.]*\w)`,
          patternMessage:
            'The property name must only contain alphanumeric characters, underscores, dots and dashes and should not be ended by dash or dot.',
          required: true
        }
      },
      {
        weight: 10,
        type: 'textfield',
        input: true,
        key: 'customOptions.imageIdField',
        label: 'Image Id Field',
        placeholder: 'e.g. profile.photoDocumentId',
        description:
          "Optional. Dotted path (in submission data) to read the image/document id from. Leave blank to use this field's own value as the image id."
      },
      {
        weight: 20,
        type: 'textfield',
        input: true,
        key: 'customOptions.apiUrl',
        label: 'Preview API URL',
        placeholder: '/api/core/documents/${imageId}/preview',
        description:
          'Optional. URL to fetch the image preview from. Use ${imageId} as a placeholder for the resolved image id. Leave blank to use the default documents/{id}/preview endpoint.'
      },
      {
        weight: 30,
        type: 'textfield',
        input: true,
        key: 'customOptions.responseDataProperty',
        label: 'Response Data Property',
        placeholder: 'dataUri',
        description:
          "Optional. Property in the JSON response (dotted paths supported) that holds the image's data URI. Leave blank to use 'dataUri'."
      }
    ]
  };
}

function conditionalTab() {
  return {
    label: 'Conditional',
    key: 'conditional',
    components: [
      {
        type: 'panel',
        title: 'Simple',
        key: 'simple-conditional',
        theme: 'default',
        weight: 105,
        components: [
          {
            type: 'select',
            input: true,
            label: 'This component should Display:',
            key: 'conditional.show',
            dataSrc: 'values',
            data: {
              values: [
                { label: 'True', value: 'true' },
                { label: 'False', value: 'false' }
              ]
            }
          },
          {
            type: 'select',
            input: true,
            label: 'When the form component:',
            key: 'conditional.when',
            dataSrc: 'custom',
            valueProperty: 'value',
            data: {
              custom(context) {
                const values: any[] = [];
                context.utils.eachComponent(
                  context.instance.options.editForm.components,
                  function (component, path) {
                    if (component.key !== context.data.key) {
                      values.push({
                        label: `${component.label || component.key} (${path})`,
                        value: path
                      });
                    }
                  }
                );
                return values;
              }
            }
          },
          {
            type: 'textfield',
            input: true,
            label: 'Has the value:',
            key: 'conditional.eq'
          }
        ]
      },
      {
        type: 'textfield',
        label: 'ACL Key',
        key: 'aclResource',
        weight: 5,
        placeholder: 'ACL Key',
        input: true
      },
      Components.EditFormUtils.javaScriptValue(
        'Advanced Conditions',
        'customConditional',
        'conditional.json',
        110,
        '<p>You must assign the <strong>show</strong> variable a boolean result.</p>' +
          '<p><strong>Note: Advanced Conditional logic will override the results of the Simple Conditional logic.</strong></p>' +
          '<h5>Example</h5><pre>show = !!data.showMe;</pre>',
        '<p><a href="http://formio.github.io/formio.js/app/examples/conditions.html" target="_blank" rel="noopener noreferrer">Click here for an example</a></p>'
      )
    ]
  };
}

class Status {
  public static readonly status = new Map();
}

export function registerImageByIdComponent(
  injector: Injector,
  screen: string
) {
  if (!Status.status.has(screen)) {
    try {
      registerCustomFormioComponent(
        COMPONENT_OPTIONS,
        ImageByIdComponent,
        injector
      );
      Status.status.set(screen, true);
    } catch (e) {
      console.warn('Error while registering ImageByIdComponent: ', screen, e);
    }
  }
}
